import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import { Settings, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { ServicesCatalogSettings } from './ServicesCatalogSettings';
import { OrganizationDataExportCard } from './OrganizationDataExportCard';

interface AuthSettings {
  passwordResetEnabled: boolean;
  sessionTimeout: number;
}

interface NotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
}

interface DataSettings {
  autoBackupEnabled: boolean;
}

interface SecuritySettings {
  twoFactorEnabled: boolean;
  ipWhitelistEnabled: boolean;
  maxLoginAttempts: number;
}

interface SystemSettingsProps {
  settingsFilter?: 'general' | 'security';
}

export function SystemSettings({ settingsFilter }: SystemSettingsProps) {
  const { userRole } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [authSettings, setAuthSettings] = useState<AuthSettings>({
    passwordResetEnabled: true,
    sessionTimeout: 30
  });
  
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailEnabled: true,
    smsEnabled: false
  });
  
  const [dataSettings, setDataSettings] = useState<DataSettings>({
    autoBackupEnabled: true
  });

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>({
    twoFactorEnabled: false,
    ipWhitelistEnabled: false,
    maxLoginAttempts: 5
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value');

      if (error) throw error;

      data?.forEach((setting) => {
        const value = setting.setting_value as Record<string, unknown>;
        switch (setting.setting_key) {
          case 'authentication':
            setAuthSettings({
              passwordResetEnabled: (value.passwordResetEnabled as boolean) ?? true,
              sessionTimeout: (value.sessionTimeout as number) ?? 30
            });
            break;
          case 'notifications':
            setNotificationSettings({
              emailEnabled: (value.emailEnabled as boolean) ?? true,
              smsEnabled: (value.smsEnabled as boolean) ?? false
            });
            break;
          case 'dataManagement':
            setDataSettings({
              autoBackupEnabled: (value.autoBackupEnabled as boolean) ?? true
            });
            break;
          case 'security':
            setSecuritySettings({
              twoFactorEnabled: (value.twoFactorEnabled as boolean) ?? false,
              ipWhitelistEnabled: (value.ipWhitelistEnabled as boolean) ?? false,
              maxLoginAttempts: (value.maxLoginAttempts as number) ?? 5
            });
            break;
        }
      });
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const updates = settingsFilter === 'security' 
        ? [
            { setting_key: 'authentication', setting_value: authSettings },
            { setting_key: 'security', setting_value: securitySettings }
          ]
        : [
            { setting_key: 'notifications', setting_value: notificationSettings },
            { setting_key: 'dataManagement', setting_value: dataSettings }
          ];

      for (const update of updates) {
        const { error } = await supabase
          .from('system_settings')
          .upsert(
            { 
              setting_key: update.setting_key, 
              setting_value: update.setting_value as unknown as Json 
            },
            { onConflict: 'setting_key' }
          );

        if (error) throw error;
      }

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (userRole !== 'super_admin' && userRole !== 'program_director' && userRole !== 'manager') {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg font-semibold mb-2">Access Restricted</p>
          <p className="text-muted-foreground">Only Super Admins and Program Directors can access system settings</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Security tab content
  if (settingsFilter === 'security') {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Authentication</CardTitle>
            <CardDescription>Manage login and session security</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Password Reset</Label>
                <p className="text-sm text-muted-foreground">
                  Allow users to reset their passwords via email
                </p>
              </div>
              <Switch
                checked={authSettings.passwordResetEnabled}
                onCheckedChange={(checked) => 
                  setAuthSettings(prev => ({ ...prev, passwordResetEnabled: checked }))
                }
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Session Timeout (minutes)</Label>
              <Input
                type="number"
                value={authSettings.sessionTimeout}
                onChange={(e) => 
                  setAuthSettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) || 30 }))
                }
                className="w-32"
              />
              <p className="text-sm text-muted-foreground">
                Users will be automatically logged out after this period of inactivity
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Access Control</CardTitle>
            <CardDescription>Advanced security policies</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Require Two-Factor Authentication</Label>
                <p className="text-sm text-muted-foreground">
                  When on, <strong>every user</strong> is blocked from the app until they
                  enroll a TOTP authenticator and verify (aal2) — regardless of role.
                  Enforced server-side via the <code>user_requires_mfa</code> database
                  check; this toggle is the source of truth.
                </p>
              </div>
              <Switch
                checked={securitySettings.twoFactorEnabled}
                onCheckedChange={(checked) =>
                  setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: checked }))
                }
              />
            </div>


            <Separator />

            <div className="space-y-2">
              <Label>Max Login Attempts</Label>
              <Input
                type="number"
                value={securitySettings.maxLoginAttempts}
                onChange={(e) => 
                  setSecuritySettings(prev => ({ ...prev, maxLoginAttempts: parseInt(e.target.value) || 5 }))
                }
                className="w-32"
              />
              <p className="text-sm text-muted-foreground">
                Lock account after this many failed login attempts
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings className="h-4 w-4 mr-2" />}
            Save Security Settings
          </Button>
        </div>
      </div>
    );
  }

  // General tab content
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>Configure notification delivery methods</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Send alert notifications via email
              </p>
            </div>
            <Switch
              checked={notificationSettings.emailEnabled}
              onCheckedChange={(checked) =>
                setNotificationSettings(prev => ({ ...prev, emailEnabled: checked }))
              }
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>SMS Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Send critical alerts via SMS (requires configuration)
              </p>
            </div>
            <Switch
              checked={notificationSettings.smsEnabled}
              onCheckedChange={(checked) =>
                setNotificationSettings(prev => ({ ...prev, smsEnabled: checked }))
              }
            />
          </div>
        </CardContent>
      </Card>

      <ServicesCatalogSettings />

      <OrganizationDataExportCard />

      <Card>
        <CardHeader>
          <CardTitle>Data Management</CardTitle>
          <CardDescription>Configure data backup and retention</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Automatic Backups</Label>
              <p className="text-sm text-muted-foreground">
                Automatically backup database daily at midnight
              </p>
            </div>
            <Switch
              checked={dataSettings.autoBackupEnabled}
              onCheckedChange={(checked) =>
                setDataSettings(prev => ({ ...prev, autoBackupEnabled: checked }))
              }
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Settings className="h-4 w-4 mr-2" />}
          Save General Settings
        </Button>
      </div>
    </div>
  );
}
